import { useEffect, useRef } from 'react';

import { useAppDispatch, useAppSelector } from '@/shared/hooks';

import { reportsModel } from '@/entities/reports';

// Loads the full-size images, masks and teeth of the compared reports that
// the store does not know yet, and drops them when the dialog closes. A pair
// is requested once per opening, so a failed request is retried on the next
// opening instead of in a loop.
export const useReportPanoramas = (
  reportIDs: string[],
  enabled: boolean,
): boolean => {
  const dispatch = useAppDispatch();
  const isPending = useAppSelector(
    reportsModel.selectors.selectIsReportPanoramasPending,
  );
  const panoramaAssetIDsByReportID = useAppSelector(
    reportsModel.selectors.selectPanoramaAssetIDsByReportID,
  );
  const requestedKeys = useRef(new Set<string>());
  // Read in the closing effect: the loaded ids must not restart it.
  const loadedReportIDsRef = useRef<string[]>([]);
  loadedReportIDsRef.current = Object.keys(panoramaAssetIDsByReportID);

  const missingIDs = reportIDs.filter(
    (reportID) => reportID && !panoramaAssetIDsByReportID[reportID],
  );
  const requestKey = missingIDs.sort().join(',');

  useEffect(() => {
    if (!enabled) {
      requestedKeys.current.clear();
      // The compared conditions and teeth share the report page slices, so
      // leaving them behind would mix them into the next opened 2D report.
      if (loadedReportIDsRef.current.length > 0) {
        dispatch(
          reportsModel.actions.clearReportPanoramas({
            reportIDs: loadedReportIDsRef.current,
          }),
        );
      }
      return;
    }
    if (!requestKey || requestedKeys.current.has(requestKey)) {
      return;
    }
    requestedKeys.current.add(requestKey);

    dispatch(
      reportsModel.thunks.listReportPanoramasWithConditions({
        ReportIDs: requestKey.split(','),
      }),
    );
  }, [dispatch, enabled, requestKey]);

  return isPending;
};
